import { httpsCallable } from "firebase/functions";
import { functions } from ".";
import { isBooleanTrue, isListEmpty, isListNotEmpty, isObjectEmpty, isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from "../utils/objectUtils";
import { getFileExt, getFileUrl } from "./StorageService";
import { compress, urltoBlob } from 'image-conversion';
import { toPhoneNumberLocal } from "../utils/stringFormatter";
import { Buffer } from 'buffer';
import User from "../models/User";
import { v4 as uuid } from 'uuid';
import Encounter from "../models/Encounter";
import Product from "../models/Product";
import ProductVariant from "../models/ProductVariant";
import { buildBelugaPatientPreferences } from "../utils/addOnPrescriptionUtils";
import { convertHeicFamilyBlobToJpeg, isHeicFamilyExtension } from "../utils/heicUtils";
import { buildBase64Images } from "../utils/imagePayloadUtils";
import CONSTANTS from '../constants';
import { KEY_UNREADABLE, KEY_WAIT_EXPIRED, waitForIdempotencyKeyRelease } from './IdempotencyKeys';

const getGender = (gender) => {
    switch (gender) {
        case 'MALE':
            return 'Male';
        case 'FEMALE':
            return 'Female';
        default:
            return 'Other';
    }
};

const getBelugaPatientPreferences = async (encounter, pharmacyRefId, testMode = false, testModeMedId = null) => {
    const products = await Product.getAll();
    const variants = await ProductVariant.getAll();
    return buildBelugaPatientPreferences(encounter, pharmacyRefId, products, variants, testMode, testModeMedId);
};

export const createVisit = async (encounter, profile, shippingAddress, pharmacyId, pharmacyRefId, visitType, QNAs = [], testMode = false) => {
    try {
        // console.log(encounter, profile, shippingAddress, pharmacyRefId, visitType, QNAs);
        if (isObjectNotEmpty(profile) && isObjectNotEmpty(encounter) && isObjectNotEmpty(encounter?.customer) && isStringNotEmpty(encounter?.refId) && isObjectNotEmpty(shippingAddress) && isStringNotEmpty(pharmacyRefId) && isStringNotEmpty(visitType) && isListNotEmpty(QNAs)) {
            const func = httpsCallable(functions, "createbelugavisit", { timeout: 6000000 });

            let medicationsString = 'None';
            let medicalConditionsString = 'None';
            let allergiesString = 'None';
            if (profile?.userId === encounter?.userId) {
                medicationsString = isListNotEmpty(profile.medications) ? profile.medications.map(i => i.name).join(';') : 'None';
                medicalConditionsString = isListNotEmpty(profile.medicalConditions) ? profile.medicalConditions.map(i => i.name).join(';') : 'None';
                allergiesString = isListNotEmpty(profile.allergies) ? profile.allergies.map(i => i.name).join(';') : 'None';
                QNAs.push({
                    question: 'What is your BMI?',
                    answer: profile?.bmi
                });
                QNAs.push({
                    question: 'What is your height (in inches)?',
                    answer: profile?.height
                });
                QNAs.push({
                    question: 'What is your weight (in lbs)?',
                    answer: profile?.weight
                });
            } else {
                let user = await User.getById(encounter?.userId);
                if (isObjectNotEmpty(user)) {
                    medicationsString = isListNotEmpty(user.medications) ? user.medications.map(i => i.name).join(';') : 'None';
                    medicalConditionsString = isListNotEmpty(user.medicalConditions) ? user.medicalConditions.map(i => i.name).join(';') : 'None';
                    allergiesString = isListNotEmpty(user.allergies) ? user.allergies.map(i => i.name).join(';') : 'None';
                    QNAs.push({
                        question: 'What is your BMI?',
                        answer: user?.bmi
                    });
                    QNAs.push({
                        question: 'What is your height (in inches)?',
                        answer: user?.height
                    });
                    QNAs.push({
                        question: 'What is your weight (in lbs)?',
                        answer: user?.weight
                    });
                }
            }

            let patientPreferences = await getBelugaPatientPreferences(encounter, pharmacyId, testMode, null); //testModeMedId can be used to override medId for testing purposes 'SMJ6F5wGaRupaeycjfMEZgodM3wcFQbA'

            let postData = {
                formObj: {
                    consentsSigned: true, // Boolean (REQUIRED)
                    firstName: encounter?.customer?.firstName, // String (REQUIRED)
                    lastName: encounter?.customer?.lastName, // String (REQUIRED)
                    dob: encounter?.customer?.dateOfBirth,  //String(MM / DD / YYYY, 18 + verified in client)(REQUIRED)
                    phone: toPhoneNumberLocal(encounter?.customer?.phone), // String(10 characters, only digits, ex. “5554567890”, valid US mobile number verified in client)(REQUIRED)
                    email: encounter?.customer?.email, // String(REQUIRED)
                    address: shippingAddress.streetAddress1 + (shippingAddress.streetAddress2 ? ' ' + shippingAddress.streetAddress2 : ''), // String(REQUIRED)
                    city: shippingAddress.city, // String(REQUIRED)
                    state: shippingAddress.state, // String(2 characters, ex. “CA”)(REQUIRED)
                    zip: shippingAddress.postalCode.substring(0, 5), // String(5 characters, only digits, ex. “54321”)(REQUIRED)
                    sex: getGender(encounter?.customer?.gender), // String(“Male”, “Female”, “Other”)(REQUIRED)
                    selfReportedMeds: medicationsString, // String(REQUIRED)
                    allergies: allergiesString, // String(REQUIRED)
                    medicalConditions: medicalConditionsString, // String(REQUIRED)
                    patientPreference: patientPreferences, // Array(Object)(REQUIRED)
                },
                pharmacyId: pharmacyRefId, // String(Innovation: “74626”)(REQUIRED)
                masterId: encounter?.refId, // String(REQUIRED)
                company: "readyRx", //“readyRx”(REQUIRED)
                visitType: visitType, // String(“weightloss”, “weightlossfollowup”)(REQUIRED)
                testMode: testMode
            };

            let QNAList = isListNotEmpty(QNAs) ? [...QNAs] : [];
            QNAList.forEach((QNA, index) => {
                postData.formObj[`Q${index + 1}`] = QNA.question;
                postData.formObj[`A${index + 1}`] = QNA.answer;
            });

            console.log('belugaVisitPostBody', postData);

            const res = await func({
                encounter: encounter,
                visitPayload: postData,
                testMode: testMode
            });

            if (isObjectNotEmpty(res.data)) {
                console.log('updated encounter', res.data);
                return res.data;
            }
        }
    } catch (err) {
        console.log(err);
    }

    return null;
};

export const uploadPhotos = async (encounterRefId, imageRefIds, testMode = false) => {
    if (isStringNotEmpty(encounterRefId) && isListNotEmpty(imageRefIds)) {
        const func = httpsCallable(functions, "uploadbelugaphotos", { timeout: 6000000 });

        // A bad stored image is skipped, never fatal: if this call rejected, the
        // encounter would stay isSynced=false and the retry loop could never converge.
        const { base64Images, failedRefIds } = await buildBase64Images(imageRefIds);
        if (isListNotEmpty(failedRefIds)) {
            console.log('uploadPhotos: could not convert, sending without', failedRefIds);
        }

        let payload = {
            masterId: encounterRefId,
            base64Images: base64Images,
            testMode: testMode
        };

        console.log('belugaUploadPhotos payload', payload);

        const res = await func({ ...payload });

        console.log('uploadPhotos response', res.data);
        return res.data;
    }

    return null;
};

const isValidCreateVisitAndUploadPhotos = (profile, encounter, shippingAddress, pharmacyRefId, visitType, QNAs) => {
    if (isObjectEmpty(profile)) {
        throw new Error('Profile is required.');
    }
    if (isObjectEmpty(encounter)) {
        throw new Error('Visit is required.');
    }
    if (isObjectEmpty(encounter?.customer)) {
        throw new Error('Customer Info is required.');
    }
    if (isStringEmpty(encounter?.refId)) {
        throw new Error('Master ID is required.');
    }
    if (isObjectEmpty(shippingAddress)) {
        throw new Error('Shipping Address is required.');
    }
    if (isStringEmpty(pharmacyRefId)) {
        throw new Error('Pharmacy is required.');
    }
    if (isStringEmpty(visitType)) {
        throw new Error('Visit type is required.');
    }
    if (isListEmpty(QNAs)) {
        throw new Error('Forms are required.');
    }

    return true;
};

const sanitizeNaN = (obj) => {
    // Handle primitive numbers
    if (typeof obj === 'number') {
        return Number.isNaN(obj) || !Number.isFinite(obj) ? 0 : obj;
    }

    // Handle string "NaN"
    if (obj === 'NaN') {
        return 0;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
        return obj.map(sanitizeNaN);
    }

    // Handle objects
    if (obj && typeof obj === 'object') {
        const cleaned = {};

        for (const key in obj) {
            cleaned[key] = sanitizeNaN(obj[key]);
        }

        return cleaned;
    }

    return obj;
};

// a submit whose response is lost - a reload, a second tab, a dropped connection - leaves the first attempt still
// running on the server, holding the consult's idempotency key, and anything arriving meanwhile is answered
// PROCESSING. the key is released the moment that attempt finishes, whether or not its photos landed, so the wait
// is on the key rather than on a timer: only then is it worth sending the photos again, and the server decides from
// the stored encounter what is left to do. if the key cannot be read at all, re-calls fall back to a minute apart.
// the window outlives the callable's own 300s timeout.
const VISIT_WAIT_WINDOW_MS = 320000;
const VISIT_RECALL_FALLBACK_MS = 60000;
const VISIT_RECALL_RETRY_MS = 5000;
const VISIT_MAX_RECALLS = 6;

export const BELUGA_VISIT_STILL_PROCESSING_ERROR_CODE = 'BELUGA_VISIT_STILL_PROCESSING';

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

const callAndAwaitInFlightVisit = async (func, payload, encounter) => {
    const deadline = Date.now() + VISIT_WAIT_WINDOW_MS;
    let result = (await func({ ...payload }))?.data;

    for (let recalls = 0; recalls < VISIT_MAX_RECALLS && result?.status === 'PROCESSING' && Date.now() < deadline; recalls += 1) {
        const outcome = await waitForIdempotencyKeyRelease(`consult:${encounter?.refId}`, deadline - Date.now());
        if (outcome === KEY_WAIT_EXPIRED) {
            break;
        }

        if (outcome === KEY_UNREADABLE) {
            await sleep(Math.min(VISIT_RECALL_FALLBACK_MS, Math.max(0, deadline - Date.now())));
        } else {
            // the first attempt has finished. one read tells whether it finished the whole job, in which case there
            // is nothing to send the photos for
            try {
                const storedEncounter = await Encounter.getById(encounter?.id);
                if (isBooleanTrue(storedEncounter?.isStarted) && isBooleanTrue(storedEncounter?.isSynced)) {
                    return storedEncounter;
                }
            } catch (error) {
                console.log('createVisitAndUploadPhotos: could not read the encounter, re-calling instead', error);
            }
        }

        try {
            result = (await func({ ...payload }))?.data;
        } catch (error) {
            // a failed re-call leaves the key free, so the next wait would return at once - space the retry instead
            result = { status: 'PROCESSING' };
            await sleep(VISIT_RECALL_RETRY_MS);
        }
    }

    if (result?.status === 'PROCESSING') {
        // the key stops a second visit, so the customer must not be told this failed or invited to submit again
        const stillProcessingError = new Error(`Your consult is still being sent to the doctor. Nothing else is needed from you - please do not submit again. If you have not heard from us shortly, please contact ${CONSTANTS.SUPPORT_EMAIL}.`);
        stillProcessingError.code = BELUGA_VISIT_STILL_PROCESSING_ERROR_CODE;
        throw stillProcessingError;
    }

    return result;
};

export const createVisitAndUploadPhotos = async (encounter, profile, shippingAddress, pharmacyId, pharmacyRefId, visitType, QNAs = [], imageRefIds, testMode = false) => {
    try {
        // console.log(encounter, profile, shippingAddress, pharmacyRefId, visitType, QNAs);
        if (isValidCreateVisitAndUploadPhotos(profile, encounter, shippingAddress, pharmacyRefId, visitType, QNAs)) {
            const func = httpsCallable(functions, "createbelugavisitanduploadphotos", { timeout: 330000 });

            let medicationsString = 'None';
            let medicalConditionsString = 'None';
            let allergiesString = 'None';
            if (profile?.userId === encounter?.userId) {
                medicationsString = isListNotEmpty(profile.medications) ? profile.medications.map(i => i.name).join(';') : 'None';
                medicalConditionsString = isListNotEmpty(profile.medicalConditions) ? profile.medicalConditions.map(i => i.name).join(';') : 'None';
                allergiesString = isListNotEmpty(profile.allergies) ? profile.allergies.map(i => i.name).join(';') : 'None';
                QNAs.push({
                    question: 'What is your BMI?',
                    answer: profile?.bmi
                });
                QNAs.push({
                    question: 'What is your height (in inches)?',
                    answer: profile?.height
                });
                QNAs.push({
                    question: 'What is your weight (in lbs)?',
                    answer: profile?.weight
                });
            } else {
                let user = await User.getById(encounter?.userId);
                if (isObjectNotEmpty(user)) {
                    medicationsString = isListNotEmpty(user.medications) ? user.medications.map(i => i.name).join(';') : 'None';
                    medicalConditionsString = isListNotEmpty(user.medicalConditions) ? user.medicalConditions.map(i => i.name).join(';') : 'None';
                    allergiesString = isListNotEmpty(user.allergies) ? user.allergies.map(i => i.name).join(';') : 'None';
                    QNAs.push({
                        question: 'What is your BMI?',
                        answer: user?.bmi
                    });
                    QNAs.push({
                        question: 'What is your height (in inches)?',
                        answer: user?.height
                    });
                    QNAs.push({
                        question: 'What is your weight (in lbs)?',
                        answer: user?.weight
                    });
                }
            }

            let patientPreferences = await getBelugaPatientPreferences(encounter, pharmacyId, false, null); //testModeMedId can be used to override medId for testing purposes 'SMJ6F5wGaRupaeycjfMEZgodM3wcFQbA'

            const { base64Images, failedRefIds } = await buildBase64Images(imageRefIds);
            if (isListNotEmpty(failedRefIds)) {
                console.log('createVisitAndUploadPhotos: could not convert, sending without', failedRefIds);
            }

            let postData = {
                formObj: {
                    consentsSigned: true, // Boolean (REQUIRED)
                    firstName: encounter?.customer?.firstName, // String (REQUIRED)
                    lastName: encounter?.customer?.lastName, // String (REQUIRED)
                    dob: encounter?.customer?.dateOfBirth,  //String(MM / DD / YYYY, 18 + verified in client)(REQUIRED)
                    phone: toPhoneNumberLocal(encounter?.customer?.phone), // String(10 characters, only digits, ex. “5554567890”, valid US mobile number verified in client)(REQUIRED)
                    email: encounter?.customer?.email, // String(REQUIRED)
                    address: shippingAddress.streetAddress1 + (shippingAddress.streetAddress2 ? ' ' + shippingAddress.streetAddress2 : ''), // String(REQUIRED)
                    city: shippingAddress.city, // String(REQUIRED)
                    state: shippingAddress.state, // String(2 characters, ex. “CA”)(REQUIRED)
                    zip: shippingAddress.postalCode.substring(0, 5), // String(5 characters, only digits, ex. “54321”)(REQUIRED)
                    sex: getGender(encounter?.customer?.gender), // String(“Male”, “Female”, “Other”)(REQUIRED)
                    selfReportedMeds: medicationsString, // String(REQUIRED)
                    allergies: allergiesString, // String(REQUIRED)
                    medicalConditions: medicalConditionsString, // String(REQUIRED)
                    patientPreference: patientPreferences, // Array(Object)(REQUIRED)
                },
                pharmacyId: pharmacyRefId, // String(Innovation: “74626”)(REQUIRED)
                masterId: encounter?.refId, // String(REQUIRED)
                company: "readyRx", //“readyRx”(REQUIRED)
                visitType: visitType // String(“weightloss”, “weightlossfollowup”)(REQUIRED)
            };

            let QNAList = isListNotEmpty(QNAs) ? [...QNAs] : [];
            QNAList.forEach((QNA, index) => {
                postData.formObj[`Q${index + 1}`] = QNA.question;
                postData.formObj[`A${index + 1}`] = QNA.answer;
            });

            console.log('belugaVisitPostBody', postData);

            const cleanedPayload = sanitizeNaN({
                encounter: encounter,
                visitPayload: postData,
                base64Images: base64Images,
                testMode: testMode
            });

            const result = await callAndAwaitInFlightVisit(func, cleanedPayload, encounter);

            if (isObjectNotEmpty(result)) {
                console.log('updated encounter', result, 'isStarted', result?.isStarted, 'isSynced', result?.isSynced);
                return result;
            }
        }
    } catch (err) {
        console.log(err);
        // every other failure here is swallowed into null, which the consult pages read as "nothing happened".
        // a visit still in flight is not that, and the customer has to be told so
        if (err?.code === BELUGA_VISIT_STILL_PROCESSING_ERROR_CODE) {
            throw err;
        }
    }

    return null;
};

export const sendBelugaMessage = async (firstName, lastName, content, encounterRefId, testMode = false) => {
    if (isStringNotEmpty(firstName) && isStringNotEmpty(lastName) && isStringNotEmpty(content) && isStringNotEmpty(encounterRefId)) {
        const func = httpsCallable(functions, "sendbelugamessage", { timeout: 6000000 });

        let payload = {
            firstName: firstName,
            lastName: lastName,
            content: content,
            isMedia: false,
            masterId: encounterRefId,
            testMode: testMode
        };

        const res = await func({ ...payload });

        // console.log('sendBelugaMessage response', res.data);
        return res.data;
    }

    return null;
};

export const sendBelugaImageMessage = async (firstName, lastName, imageRefId, encounterRefId, testMode = false) => {
    if (isStringNotEmpty(firstName) && isStringNotEmpty(lastName) && isStringNotEmpty(imageRefId) && isStringNotEmpty(encounterRefId)) {
        const func = httpsCallable(functions, "sendbelugamessage", { timeout: 6000000 });

        const ext = getFileExt(imageRefId);
        let url = await getFileUrl(imageRefId);
        console.log(url);
        let blob = await urltoBlob(url);
        console.log(blob);

        if (isHeicFamilyExtension(ext)) {
            blob = await convertHeicFamilyBlobToJpeg(blob);
        }

        let compressedBlob = await compress(blob, {
            quality: 0.8,
            type: "image/jpeg",
            width: 1000
        });
        console.log(compressedBlob);
        let arrayBuffer = await compressedBlob.arrayBuffer();
        let base64Image = Buffer.from(arrayBuffer).toString('base64');

        let payload = {
            firstName: firstName,
            lastName: lastName,
            content: base64Image,
            isMedia: true,
            masterId: encounterRefId,
            testMode: testMode
        };

        const res = await func({ ...payload });

        // console.log('sendBelugaImageMessage response', res.data);
        return res.data;
    }

    return null;
};

export const updateBelugaVisit = async (encounter, pharmacyId, pharmacyRefId, testMode = false) => {
    try {
        if (isObjectNotEmpty(encounter) && isStringNotEmpty(encounter?.refId) && isStringNotEmpty(pharmacyRefId)) {
            const func = httpsCallable(functions, "updatebelugavisit", { timeout: 6000000 });

            let patientPreferences = await getBelugaPatientPreferences(encounter, pharmacyId, testMode, null); //testModeMedId can be used to override medId for testing purposes 'SMJ6F5wGaRupaeycjfMEZgodM3wcFQbA'

            let postData = {
                patientPreference: patientPreferences, // Array(Object)(REQUIRED)
                pharmacyId: pharmacyRefId, // String(Innovation: “74626”)(REQUIRED)
                masterId: encounter?.refId // String(REQUIRED)
            };

            console.log('belugaVisitPostBody', postData);

            const res = await func({
                visitPayload: postData,
                testMode: testMode
            });

            if (isObjectNotEmpty(res.data)) {
                console.log('updated encounter', res.data);
                return res.data;
            }
        }
    } catch (err) {
        console.log(err);
    }

    return null;
};

export const cancelBelugaVisit = async (encounterRefId, testMode = false) => {
    try {
        if (isStringNotEmpty(encounterRefId)) {
            const func = httpsCallable(functions, "cancelbelugavisit", { timeout: 6000000 });
            const payload = { masterId: encounterRefId, testMode: testMode };
            const res = await func({ ...payload });
            console.log('cancelBelugaVisit response', res.data);
            return res.data;
        }
    } catch (err) {
        console.log(err);
    }

    return null;
};

function containsInvalidJsonValue(obj) {
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const value = obj[key];
            if (key === 'paidForAmount') {
                console.log('paidForAmount raw:', value, 'typeof:', typeof value, 'isNaN:', Number.isNaN(value));
            }
            if (typeof value === 'number' && (Number.isNaN(value) || !Number.isFinite(value))) {
                console.error(`Invalid value '${value}' found at key: ${key}`);
                console.log('Look at the whole object here:', obj);
                return true;
            }
            if (typeof value === 'object' && value !== null) {
                if (containsInvalidJsonValue(value)) {
                    return true;
                }
            }
        }
    }
    return false;
}

export const addNewBelugaPrescriptions = async (encounter, docFirstName, docLastName, docNpi, medsPrescribed, testMode = false, specificFillAt = null, overrideOffsetDelay = false, rxPdf = null, providerSignature = null) => {
    try {
        if (isObjectNotEmpty(encounter) && isListNotEmpty(medsPrescribed) && isStringNotEmpty(docFirstName) && isStringNotEmpty(docLastName) && isStringNotEmpty(docNpi)) {
            const func = httpsCallable(functions, "addnewbelugaprescriptions", { timeout: 6000000 });

            let postBody = {
                encounter: encounter,
                docFirstName: docFirstName,
                docLastName: docLastName,
                docNpi: docNpi,
                medsPrescribed: medsPrescribed,
                testMode: testMode,
                specificFillAt: specificFillAt,
                overrideOffsetDelay: overrideOffsetDelay,
                rxPdf: rxPdf,
                providerSignature: providerSignature
            };

            console.log('addNewBelugaPrescriptions postBody', { ...postBody, rxPdf: isStringNotEmpty(rxPdf) ? '[base64 omitted]' : null, providerSignature: isStringNotEmpty(providerSignature) ? '[base64 omitted]' : null });

            if (containsInvalidJsonValue(postBody)) {
                console.error('Invalid JSON value found in postBody');
                return false;
            }

            const res = await func(postBody);

            return isBooleanTrue(res.data);
        }
    } catch (err) {
        console.log(err);
    }

    return false;
};
